/**
 * Time utility functions for widgets
 * Uses dayjs for advanced time operations
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * Format time in 12-hour or 24-hour format
 */
export function formatTime(
  date: Date | number = new Date(),
  format: '12h' | '24h' = '24h',
  showSeconds = true
): string {
  const timeFormat = format === '12h' ? 'hh:mm' : 'HH:mm';
  const fullFormat = showSeconds ? `${timeFormat}:ss` : timeFormat;
  const formattedTime = dayjs(date).format(fullFormat);

  if (format === '12h') {
    const period = dayjs(date).format('A');
    return `${formattedTime} ${period}`;
  }

  return formattedTime;
}

/**
 * Format date and time
 */
export function formatDateTime(
  date: Date | number = new Date(),
  dateFormat = 'MMM DD, YYYY',
  timeFormat: '12h' | '24h' = '24h'
): string {
  const formattedDate = dayjs(date).format(dateFormat);
  const formattedTime = formatTime(date, timeFormat);
  return `${formattedDate} ${formattedTime}`;
}

/**
 * Get current time in a specific timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  return dayjs().tz(timezone).toDate();
}

/**
 * Format time for a specific timezone
 */
export function formatTimeInTimezone(
  timezone: string,
  format: '12h' | '24h' = '24h',
  showSeconds = true,
  date: Date | number = new Date()
): string {
  const time = dayjs(date).tz(timezone);
  const timeFormat = format === '12h' ? 'hh:mm' : 'HH:mm';
  const fullFormat = showSeconds ? `${timeFormat}:ss` : timeFormat;
  const formattedTime = time.format(fullFormat);

  if (format === '12h') {
    const period = time.format('A');
    return `${formattedTime} ${period}`;
  }

  return formattedTime;
}

/**
 * Get timezone offset string (e.g., "UTC+5:30")
 */
export function getTimezoneOffset(timezone: string): string {
  const offset = dayjs().tz(timezone).utcOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';

  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }

  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Format duration in seconds to HH:mm:ss
 */
export function formatDuration(seconds: number, showHours = true): string {
  const duration = dayjs.duration(seconds, 'seconds');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const secs = duration.seconds();

  if (!showHours && hours === 0) {
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Format elapsed time in milliseconds (for stopwatch)
 */
export function formatElapsedTime(
  milliseconds: number,
  showMilliseconds = false
): string {
  const duration = dayjs.duration(milliseconds);
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();
  const ms = duration.milliseconds();

  const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  if (showMilliseconds) {
    return `${timeStr}.${String(ms).padStart(3, '0')}`;
  }

  return timeStr;
}

/**
 * Parse time string (HH:mm) to total minutes since midnight
 */
export function parseTimeToMinutes(timeString: string): number {
  const parts = timeString.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  return hours * 60 + minutes;
}

/**
 * Format minutes since midnight to time string (HH:mm)
 */
export function formatMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Check if a time has passed for today
 */
export function hasTimePassed(timeString: string): boolean {
  const targetMinutes = parseTimeToMinutes(timeString);
  const now = dayjs();
  const currentMinutes = now.hour() * 60 + now.minute();
  return currentMinutes >= targetMinutes;
}

/**
 * Get time until next alarm
 */
export function getTimeUntilAlarm(timeString: string, days: number[]): string {
  const now = dayjs();
  const currentDay = now.day(); // 0-6 (Sunday-Saturday)
  const targetMinutes = parseTimeToMinutes(timeString);
  const currentMinutes = now.hour() * 60 + now.minute();
  const alarmDays = days.length > 0 ? days : [0, 1, 2, 3, 4, 5, 6];

  // Check if alarm is for today and hasn't passed
  if (alarmDays.includes(currentDay) && currentMinutes < targetMinutes) {
    const diff = targetMinutes - currentMinutes;
    return dayjs.duration(diff, 'minutes').humanize();
  }

  // Find next day alarm is scheduled
  let daysAhead = 1;
  let nextDay = (currentDay + 1) % 7;

  while (!alarmDays.includes(nextDay) && daysAhead < 7) {
    daysAhead++;
    nextDay = (currentDay + daysAhead) % 7;
  }

  if (daysAhead >= 7) {
    return 'Not scheduled';
  }

  const totalMinutesAhead = daysAhead * 24 * 60 + targetMinutes - currentMinutes;
  return dayjs.duration(totalMinutesAhead, 'minutes').humanize();
}

/**
 * Get day name from number (0-6)
 */
export function getDayName(day: number, short = false): string {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const result = short ? shortDays[day] : days[day];
  return result ?? 'Unknown';
}

/**
 * Get current timestamp
 */
export function now(): number {
  return Date.now();
}

/**
 * Get dayjs instance (for advanced use cases)
 */
export { dayjs };
